import { type RefObject, useEffect, useState } from 'react';

/** Gutter (CSS px) reserved around the fitted image so transformer anchors at
 *  the image edge stay grabbable. */
export const STAGE_PAD = 16;

export type Fit = {
  /** Container content size in CSS px. The Stage canvas matches this exactly
   *  so zoomed/panned content can extend across the full visible area. */
  containerW: number;
  containerH: number;
  /** Scale factor that fits the source image inside (container - 2*STAGE_PAD). */
  fitScale: number;
};

export function useEditorFit(
  containerRef: RefObject<HTMLDivElement | null>,
  imageW: number,
  imageH: number,
): Fit {
  const [size, setSize] = useState<Fit>({ containerW: 1, containerH: 1, fitScale: 1 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = () => {
      const cw = Math.max(1, el.clientWidth);
      const ch = Math.max(200, el.clientHeight);
      const usableW = Math.max(1, cw - STAGE_PAD * 2);
      const usableH = Math.max(1, ch - STAGE_PAD * 2);
      const fitScale = Math.min(usableW / Math.max(1, imageW), usableH / Math.max(1, imageH));
      setSize({ containerW: cw, containerH: ch, fitScale });
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, imageW, imageH]);
  return size;
}
